import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';



// Initial form data for Soft Tissue form
const INITIAL_SOFT_TISSUE_DATA = {
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  hernia: {
    evaluated: false,
    hernias: [{
      location: '',
      findings: '',
      containsFat: false,
      containsBowel: false,
      containsFluid: false,
      maximumSize: ''
    }]
  },
  mass: {
    evaluated: false,
    masses: [{
      location: '',
      composition: '',
      size1: '',
      size2: '',
      size3: ''
    }]
  },
  other: {
    evaluated: false,
    findings: ''
  }
};

function SoftTissueForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Hernia, 1=Mass
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  // Use the custom hook
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_SOFT_TISSUE_DATA,
      technologist: defaultTech
    },  
    'Soft Tissue',
    {
    token, facilityInfo
    }
  );

  // Update technologist when defaultTech changes
  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const addHernia = useCallback(() => {
    const newHernia = {
      location: '',
      findings: '',
      containsFat: false,
      containsBowel: false,
      containsFluid: false,
      maximumSize: ''
    };
    updateNestedField('hernia.hernias', [...formData.hernia.hernias, newHernia]);
  }, [formData.hernia.hernias, updateNestedField]);

  const removeHernia = useCallback((index) => {
    const newHernias = formData.hernia.hernias.filter((_, i) => i !== index);
    updateNestedField('hernia.hernias', newHernias);
  }, [formData.hernia.hernias, updateNestedField]);

  const updateHernia = useCallback((index, field, value) => {
    const newHernias = formData.hernia.hernias.map((h, i) => 
      i === index ? { ...h, [field]: value } : h
    );
    updateNestedField('hernia.hernias', newHernias);
  }, [formData.hernia.hernias, updateNestedField]);

  const addMass = useCallback(() => {
    const newMass = {
      location: '',
      composition: '',
      size1: '',
      size2: '',
      size3: ''
    };
    updateNestedField('mass.masses', [...formData.mass.masses, newMass]);
  }, [formData.mass.masses, updateNestedField]);

  const removeMass = useCallback((index) => {
    const newMasses = formData.mass.masses.filter((_, i) => i !== index);
    updateNestedField('mass.masses', newMasses);
  }, [formData.mass.masses, updateNestedField]);

  const updateMass = useCallback((index, field, value) => {
    const newMasses = formData.mass.masses.map((m, i) => 
      i === index ? { ...m, [field]: value } : m
    );
    updateNestedField('mass.masses', newMasses);
  }, [formData.mass.masses, updateNestedField]);

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Hernia
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Mass
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Hernia Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)' }}>Hernia</h3>
                
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.hernia.evaluated}
                      onChange={(e) => updateNestedField('hernia.evaluated', e.target.checked)}
                    />
                    {' '}Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.hernia.evaluated ? 1 : 0.5, pointerEvents: formData.hernia.evaluated ? 'auto' : 'none' }}>
            {formData.hernia.hernias.map((hernia, index) => (
              <div key={index} style={{ marginBottom: '20px', padding: '15px', border: '1px solid var(--color-border)', borderRadius: '5px', backgroundColor: 'var(--color-surface)' }}>
                {formData.hernia.hernias.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0 }}>Hernia {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeHernia(index)}
                      disabled={!formData.hernia.evaluated}
                      style={{ padding: '5px 10px', backgroundColor: 'var(--color-danger-solid)', color: 'var(--color-input-bg)', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                )}
                
                <div className="form-group">
                  <label>Location:</label>
                  <select
                    value={hernia.location}
                    onChange={(e) => updateHernia(index, 'location', e.target.value)}
                    disabled={!formData.hernia.evaluated}
                  >
                    <option value="">Select location</option>
                    <option value="Periumbilical">Periumbilical</option>
                    <option value="Right Groin">Right Groin</option>
                    <option value="Left Groin">Left Groin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Findings:</label>
                  <select
                    value={hernia.findings}
                    onChange={(e) => updateHernia(index, 'findings', e.target.value)}
                    disabled={!formData.hernia.evaluated}
                  >
                    <option value="">Select findings</option>
                    <option value="No hernia visualized">No hernia visualized</option>
                    <option value="Nonreducible hernia">Nonreducible hernia</option>
                    <option value="Hernia visualized only with valsalva">Hernia visualized only with valsalva</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={hernia.containsFat}
                      onChange={(e) => updateHernia(index, 'containsFat', e.target.checked)}
                      disabled={!formData.hernia.evaluated}
                    />
                    {' '}Contains Fat
                  </label>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={hernia.containsBowel}
                      onChange={(e) => updateHernia(index, 'containsBowel', e.target.checked)}
                      disabled={!formData.hernia.evaluated}
                    />
                    {' '}Contains Bowel
                  </label>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={hernia.containsFluid}
                      onChange={(e) => updateHernia(index, 'containsFluid', e.target.checked)}
                      disabled={!formData.hernia.evaluated}
                    />
                    {' '}Contains Fluid
                  </label>
                </div>

                <div className="form-group">
                  <label>Maximum Size (cm):</label>
                  <input
                    type="number"
                    step="0.1"
                    value={hernia.maximumSize}
                    onChange={(e) => updateHernia(index, 'maximumSize', e.target.value)}
                    placeholder="Enter size in cm"
                    disabled={!formData.hernia.evaluated}
                  />
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addHernia}
              disabled={!formData.hernia.evaluated}
              style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-solid)', color: 'var(--color-input-bg)', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' }}
            >
              + Add Additional Hernia
            </button>
                </div>
              </div>
            )}

            {/* Mass Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)' }}>Mass</h3>
                
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.mass.evaluated}
                      onChange={(e) => updateNestedField('mass.evaluated', e.target.checked)}
                    />
                    {' '}Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.mass.evaluated ? 1 : 0.5, pointerEvents: formData.mass.evaluated ? 'auto' : 'none' }}>
            {formData.mass.masses.map((mass, index) => (
              <div key={index} style={{ marginBottom: '20px', padding: '15px', border: '1px solid var(--color-border)', borderRadius: '5px', backgroundColor: 'var(--color-surface)' }}>
                {formData.mass.masses.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0 }}>Mass {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeMass(index)}
                      disabled={!formData.mass.evaluated}
                      style={{ padding: '5px 10px', backgroundColor: 'var(--color-danger-solid)', color: 'var(--color-input-bg)', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                )}
                
                <div className="form-group">
                  <label>Location:</label>
                  <input
                    type="text"
                    value={mass.location}
                    onChange={(e) => updateMass(index, 'location', e.target.value)}
                    placeholder="Enter mass location"
                    disabled={!formData.mass.evaluated}
                  />
                </div>

                <div className="form-group">
                  <label>Composition:</label>
                  <select
                    value={mass.composition}
                    onChange={(e) => updateMass(index, 'composition', e.target.value)}
                    disabled={!formData.mass.evaluated}
                  >
                    <option value="">Select composition</option>
                    <option value="Lymph node">Lymph node</option>
                    <option value="Solid - isoechoic to fat">Solid - isoechoic to fat</option>
                    <option value="Solid - hypoechoic to fat">Solid - hypoechoic to fat</option>
                    <option value="Solid - hyperechoic to fat">Solid - hyperechoic to fat</option>
                    <option value="Solid - heterogeneous">Solid - heterogeneous</option>
                    <option value="Cystic - simple">Cystic - simple</option>
                    <option value="Cystic - complex">Cystic - complex</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Size (cm):</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="number"
                      step="0.1"
                      value={mass.size1}
                      onChange={(e) => updateMass(index, 'size1', e.target.value)}
                      placeholder="Length"
                      disabled={!formData.mass.evaluated}
                      style={{ width: '100px' }}
                    />
                    <input
                      type="number"
                      step="0.1"
                      value={mass.size2}
                      onChange={(e) => updateMass(index, 'size2', e.target.value)}
                      placeholder="Width"
                      disabled={!formData.mass.evaluated}
                      style={{ width: '100px' }}
                    />
                    <input
                      type="number"
                      step="0.1"
                      value={mass.size3}
                      onChange={(e) => updateMass(index, 'size3', e.target.value)}
                      placeholder="Depth"
                      disabled={!formData.mass.evaluated}
                      style={{ width: '100px' }}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addMass}
              disabled={!formData.mass.evaluated}
              style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-solid)', color: 'var(--color-input-bg)', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' }}
            >
              + Add Additional Mass
            </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Other Section */}
        <div className="form-section">
          <h3>Other</h3>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.other.evaluated}
                onChange={(e) => updateNestedField('other.evaluated', e.target.checked)}
              />
              {' '}Evaluated
            </label>
          </div>

          <div style={{ opacity: formData.other.evaluated ? 1 : 0.5, pointerEvents: formData.other.evaluated ? 'auto' : 'none' }}>
            <div className="form-group">
              <label>Findings:</label>
              <textarea
                rows="8"
                value={formData.other.findings}
                onChange={(e) => updateNestedField('other.findings', e.target.value)}
                placeholder="Enter additional findings..."
                disabled={!formData.other.evaluated}
              />
            </div>
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default SoftTissueForm;
